import { pushGtmEvent } from '@/src/analytics/gtm'
import { useGetSrpResultsQuery } from '@/src/api/rtkQuery/srpFlightResults'
import useGetSearchQueryURL from '@/src/hooks/srp/getSearchQueryURL'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import { getGtmPageCountry } from '@/src/utils/analytics/gtm/getGtmPageCountry'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getUserAuthValues } from '@/src/utils/auth'
import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { getFormattedDate } from '@/src/utils/date'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'

const useSrpGtmEvent = () => {
    const locale = useLocale()
    const { urlQueryObj } = useGetSearchParamsObj()

    const t = useTranslations('metadata')
    const deviceType = getDeviceType()
    const platform = getPlatformName(deviceType)
    const isSignedIn = isUserSignedIn()
    const { email } = getUserAuthValues() || {}
    const userEmailPhone = isSignedIn ? `{"user_email":"${email || 'not_informed'}","user_phone":"not_informed"}` : null
    // Extract search parameters - keeping original destructuring pattern
    const { from, to, flightClass, depart_date, return_date, adults, childs, infants, item } = useMemo(() => {
        const {
            from = '',
            to = '',
            class: flightClass,
            depart_date,
            return_date,
            intl,
            adults,
            childs,
            infants
        } = urlQueryObj || {}
        const item = `${from}-${to}-${return_date ? 'rt' : 'ow'}`

        return {
            from,
            to,
            flightClass,
            depart_date,
            return_date,
            intl,
            adults: Number(adults),
            childs: Number(childs),
            infants: Number(infants),
            item
        }
    }, [urlQueryObj])

    const { query, error: pastDateError } = useGetSearchQueryURL()
    const { fromCity = '', toCity = '' } = useGetSrpResultsQuery(
        { query },
        {
            skip: !query || !!pastDateError,
            selectFromResult: result => {
                const { jsons } = result.data || {}
                const { ap } = jsons || {}
                const fromCity = ap?.[from]?.c || ''
                const toCity = ap?.[to]?.c || ''
                return { fromCity, toCity }
            }
        }
    )

    const pushEventToDataLayer = (payload: any) => {
        // TODO: When writing for Cleartrip, we can define a function here that creates the payload.
        // Based on it, for Cleartrip or Flyin we can have two functions and we will use that to get the payload.

        const finalPayload = {
            PageType: 'FlightResultPage',
            item,
            trip_type: return_date ? 'round_trip' : 'one_way_trip',
            source_airport_code: from.toLowerCase(),
            destination_airport_code: to.toLowerCase(),
            source_airport_city: fromCity,
            destination_airport_city: toCity,
            travelers_adult_count: adults,
            travelers_children_count: childs,
            travelers_infants_count: infants,
            travelers_total_count: adults + childs + infants,
            departing_departure_date: getFormattedDate(depart_date, 'YYYY/MM/DD'),
            returning_departure_date: return_date
                ? getFormattedDate(return_date, 'YYYY/MM/DD')
                : 'no_returning_departure_date',
            flight_class: flightClass.toLowerCase(),
            // city_id: '50257',
            product_category: 'flights',
            page_referrer: document.referrer || 'no_page_referrer',
            page_language: locale,
            page_url: window.location.href,
            page_sub_category: 'flights',
            page_path: window.location.pathname,
            page_domain: window.location.hostname,
            page_title: t('title'),
            page_country: getGtmPageCountry(window.location.hostname),
            page_currency: getUserSelectedCurrencyCode()?.toLowerCase() || '',
            user_email_phone: userEmailPhone,
            user_logged_in: isSignedIn ? 'yes' : 'no',
            is_new_flow: 'yes',
            platform,
            ...payload
        }

        pushGtmEvent(finalPayload)
    }

    return {
        pushEventToDataLayer
    }
}

export default useSrpGtmEvent
